import React, { useEffect, useRef, useState } from 'react';
import { SendIcon, ArchiveIcon, RotateCcwIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useRegistry } from '../../contexts/RegistryContext';
import { formatWhen } from '../../utils/adminFormat';
export function MessagesPanel() {
  const registry = useRegistry();
  const {
    replyToThread,
    markThreadRead,
    setThreadStatus
  } = registry;
  /** Inbound conversations only — outreach you started lives in its own tab. */
  const threads = registry.threads.filter(t => t.initiatedBy !== 'owner');
  const [selectedId, setSelectedId] = useState<string | null>(threads[0]?.id ?? null);
  const [draft, setDraft] = useState('');
  const logRef = useRef<HTMLDivElement>(null);
  const selected = threads.find(t => t.id === selectedId) ?? null;
  useEffect(() => {
    if (!selectedId && threads.length) setSelectedId(threads[0].id);
  }, [selectedId, threads]);
  useEffect(() => {
    if (selected?.unreadForOwner) markThreadRead(selected.id);
  }, [markThreadRead, selected]);
  useEffect(() => {
    logRef.current?.scrollTo({
      top: logRef.current.scrollHeight
    });
  }, [selected?.messages.length, selectedId]);
  if (!threads.length) {
    return <p className="mt-10 border border-dashed border-navy-200 bg-white p-10 text-center font-sans text-[0.875rem] text-charcoal-500" data-id="element-195" data-mp="vgpvfzlqk8tg">
        No conversations yet. Messages sent from the public contact page arrive here, and you can reply from this
        screen.
      </p>;
  }
  return <div className="mt-8 grid gap-6 lg:grid-cols-[20rem_1fr]" data-id="element-196" data-mp="7r5axc9xp3i2">
      <ul className="max-h-[32rem] divide-y divide-navy-100 overflow-y-auto border border-navy-100 bg-white" data-id="element-197" data-mp="cp6ppl0bugnq">
        {threads.map(t => <li key={t.id} data-id="element-198" data-mp="hgouyl9dv3ei">
            <button type="button" onClick={() => setSelectedId(t.id)} aria-current={t.id === selectedId ? 'true' : undefined} className={`w-full border-l-2 px-4 py-3.5 text-left transition-colors duration-120 ${t.id === selectedId ? 'border-gold bg-parchment' : 'border-transparent hover:bg-parchment/60'}`} data-id="element-199" data-mp="hhzxkhr3e541">
              <span className="flex items-center justify-between gap-2" data-id="element-200" data-mp="390w9j9lm1207">
                <span className="truncate font-serif text-[0.9375rem] text-ink" data-id="element-201" data-mp="3mtlz1o0ckp22">{t.contactName}</span>
                {t.unreadForOwner ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-label="Unread" data-id="element-202" data-mp="2u56k11h7u6qq" /> : null}
              </span>
              <span className="mt-0.5 block truncate font-sans text-[0.8125rem] text-charcoal-500" data-id="element-203" data-mp="2q564temalrim">{t.subject}</span>
              <span className="mt-1 block font-sans text-[0.6875rem] uppercase tracking-[0.1em] text-charcoal-300" data-id="element-204" data-mp="344xfb5dloprv">
                {t.organization || 'No organization given'} · {t.status === 'closed' ? 'Closed' : 'Open'}
              </span>
            </button>
          </li>)}
      </ul>

      {selected ? <div className="flex min-h-[24rem] flex-col border border-navy-100 bg-white" data-id="element-205" data-mp="egocyninnk4p">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-navy-100 px-5 py-4" data-id="element-206" data-mp="1we1ircfiqf1k">
            <div data-id="element-207" data-mp="218nmquj9t5bm">
              <h2 className="font-serif text-[1.125rem] text-ink" data-id="element-208" data-mp="1nbij11714ijj">{selected.subject}</h2>
              <p className="mt-1 font-sans text-[0.8125rem] text-charcoal-500" data-id="element-209" data-mp="1sabzn2hm4pnx">
                {selected.contactName}
                {selected.organization ? ` · ${selected.organization}` : ''} ·{' '}
                <a href={`mailto:${selected.contactEmail}`} className="underline underline-offset-2" data-id="element-210" data-mp="2k19otwkrts4n">
                  {selected.contactEmail}
                </a>
              </p>
              <p className="mt-1 font-sans text-[0.6875rem] uppercase tracking-[0.1em] text-charcoal-300" data-id="element-211" data-mp="268a8xth2tvwk">
                {selected.reference}
              </p>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setThreadStatus(selected.id, selected.status === 'open' ? 'closed' : 'open')} data-id="element-212" data-mp="2yxh6ap4sofg8">
              {selected.status === 'open' ? <>
                  <ArchiveIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-213" data-mp="1a3jm0onaqn8v" />
                  Close
                </> : <>
                  <RotateCcwIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-214" data-mp="113wkm2ukhhft" />
                  Reopen
                </>}
            </Button>
          </div>

          <div ref={logRef} className="flex-1 space-y-4 overflow-y-auto bg-parchment px-5 py-5" data-id="element-215" data-mp="1xtdvqxcbjvow">
            {selected.messages.map(m => <div key={m.id} className={m.from === 'owner' ? 'ml-auto max-w-[80%] bg-ink px-4 py-3 text-parchment' : 'max-w-[85%] border-l-2 border-gold bg-white px-4 py-3'} data-id="element-216" data-mp="1p1bzzcd733ky">
                <p className={`font-sans text-[0.875rem] leading-relaxed ${m.from === 'owner' ? 'text-parchment' : 'text-charcoal-700'}`} data-id="element-217" data-mp="2cqwla1wtmcxe">
                  {m.body}
                </p>
                <p className={`mt-2 font-sans text-[0.6875rem] ${m.from === 'owner' ? 'text-navy-200' : 'text-charcoal-300'}`} data-id="element-218" data-mp="2p3ryqyxfd76n">
                  {m.from === 'owner' ? 'You' : selected.contactName} · {formatWhen(m.sentAt)}
                </p>
              </div>)}
          </div>

          <form className="flex items-end gap-2 border-t border-navy-100 px-4 py-3" onSubmit={e => {
        e.preventDefault();
        if (!draft.trim()) return;
        replyToThread(selected.id, draft.trim());
        setDraft('');
      }} data-id="element-219" data-mp="37z8kvre11csk">
            <label htmlFor="owner-reply" className="sr-only" data-id="element-220" data-mp="3ltj4jub62grr">
              Reply to {selected.contactName}
            </label>
            <textarea id="owner-reply" rows={2} value={draft} onChange={e => setDraft(e.target.value)} placeholder="Write a reply…" className="min-w-0 flex-1 resize-y border border-navy-100 px-3 py-2 font-sans text-[0.875rem] text-ink placeholder:text-charcoal-200 transition-colors duration-120 hover:border-navy-300 focus:border-navy-500" data-id="element-221" data-mp="3qool0dlsv38g" />
            <Button type="submit" size="sm" disabled={!draft.trim()} data-id="element-222" data-mp="2abzgbqfqtqss">
              <SendIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-223" data-mp="25dy15glfubml" />
              Send
            </Button>
          </form>
        </div> : null}
    </div>;
}